<template>
  <CompanyAppLayout
    page-title="On Site Tools"
    page-subtitle="Work orders, field tickets, fleet, sites, and inventory"
  >
    <div class="space-y-6 pt-4">
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          :class="activeTab === tab.id
            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
            : 'bg-slate-800/60 text-slate-300 border border-slate-700 hover:border-slate-500'"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
          <span class="ml-2 text-xs text-slate-400">{{ counts[tab.id] ?? 0 }}</span>
        </button>
      </div>

      <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <div class="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h2 class="text-xl font-semibold text-white">{{ currentTab.label }}</h2>
          <button
            v-if="currentTab.canCreate"
            class="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium"
            @click="createItem"
          >
            Add {{ currentTab.singular }}
          </button>
        </div>

        <p v-if="error" class="text-red-400 mb-4">{{ error }}</p>
        <p v-else-if="loading" class="text-slate-400">Loading…</p>

        <div v-else-if="!rows.length" class="text-slate-400 py-10 text-center">
          No {{ currentTab.label.toLowerCase() }} yet. Use Add to create the first record.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm text-left">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th v-for="col in currentTab.columns" :key="col.key" class="py-2 pr-4 font-medium">{{ col.label }}</th>
                <th v-if="currentTab.canCreate" class="py-2 pr-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="border-b border-slate-800/80 text-slate-200"
                :class="{ 'cursor-pointer hover:bg-slate-700/30': activeTab === 'field-tickets' }"
                @click="activeTab === 'field-tickets' && row.id && router.push(`/company/field-tickets/${row.id}`)"
              >
                <td v-for="col in currentTab.columns" :key="col.key" class="py-3 pr-4">
                  {{ row[col.key] ?? '—' }}
                </td>
                <td v-if="currentTab.canCreate" class="py-3 pr-4">
                  <button
                    v-if="canDelete(row)"
                    class="text-red-400 hover:text-red-300 font-medium disabled:opacity-50"
                    :disabled="deletingId === row.id"
                    @click.stop="deleteItem(row)"
                  >{{ deletingId === row.id ? 'Deleting…' : 'Delete' }}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

type TabId = 'work-orders' | 'field-tickets' | 'sites' | 'trucking' | 'loads' | 'rentals' | 'parts' | 'chemicals' | 'jsa'

const route = useRoute()
const router = useRouter()

const tabs: Array<{
  id: TabId
  label: string
  singular: string
  endpoint: string
  listKey: string
  canCreate: boolean
  columns: Array<{ key: string; label: string }>
  createBody: () => Record<string, unknown>
}> = [
  {
    id: 'work-orders',
    label: 'Work Orders',
    singular: 'Work Order',
    endpoint: '/api/company/work-orders',
    listKey: 'workOrders',
    canCreate: true,
    columns: [
      { key: 'number', label: 'Number' },
      { key: 'title', label: 'Title' },
      { key: 'customer', label: 'Customer' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
    ],
    createBody: () => ({ title: 'New Work Order', customerName: 'Customer', priority: 'normal', description: '' }),
  },
  {
    id: 'field-tickets',
    label: 'Field Tickets',
    singular: 'Ticket',
    endpoint: '/api/company/field-tickets',
    listKey: 'fieldTickets',
    canCreate: true,
    columns: [
      { key: 'number', label: 'Number' },
      { key: 'title', label: 'Title' },
      { key: 'status', label: 'Status' },
      { key: 'laborHours', label: 'Hours' },
      { key: 'totalAmount', label: 'Total' },
    ],
    createBody: () => ({ title: 'Field Ticket', laborHours: 8, laborRate: 85, materialsCost: 0, totalAmount: 680 }),
  },
  {
    id: 'sites',
    label: 'Field Sites',
    singular: 'Site',
    endpoint: '/api/company/field-sites',
    listKey: 'sites',
    canCreate: true,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'apiNumber', label: 'API #' },
      { key: 'location', label: 'Location' },
      { key: 'status', label: 'Status' },
    ],
    createBody: () => ({ name: 'New Field Site', apiNumber: '', location: 'Williston Basin' }),
  },
  {
    id: 'trucking',
    label: 'Trucking Fleet',
    singular: 'Unit',
    endpoint: '/api/company/trucking',
    listKey: 'fleet',
    canCreate: true,
    columns: [
      { key: 'unitNumber', label: 'Unit' },
      { key: 'driver', label: 'Driver' },
      { key: 'status', label: 'Status' },
    ],
    createBody: () => ({ unitNumber: `T-${Date.now().toString().slice(-4)}`, driverName: 'Unassigned' }),
  },
  {
    id: 'loads',
    label: 'Trucking Loads',
    singular: 'Load',
    endpoint: '/api/company/trucking/loads',
    listKey: 'loads',
    canCreate: true,
    columns: [
      { key: 'loadNumber', label: 'Load #' },
      { key: 'commodity', label: 'Commodity' },
      { key: 'pickup', label: 'Pickup' },
      { key: 'dropoff', label: 'Dropoff' },
      { key: 'status', label: 'Status' },
    ],
    createBody: () => ({
      loadNumber: `L-${Date.now().toString().slice(-5)}`,
      commodity: 'Crude',
      pickup: 'Battery',
      dropoff: 'Terminal',
      quantity: 180,
      unit: 'bbl',
    }),
  },
  {
    id: 'rentals',
    label: 'Rentals',
    singular: 'Equipment',
    endpoint: '/api/company/rentals',
    listKey: 'equipment',
    canCreate: true,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'assetTag', label: 'Asset Tag' },
      { key: 'category', label: 'Category' },
      { key: 'dailyRate', label: 'Daily Rate' },
      { key: 'status', label: 'Status' },
    ],
    createBody: () => ({ name: 'Rental Asset', assetTag: `A-${Date.now().toString().slice(-4)}`, category: 'equipment', dailyRate: 250, location: 'Yard' }),
  },
  {
    id: 'parts',
    label: 'Parts Inventory',
    singular: 'Part',
    endpoint: '/api/company/parts',
    listKey: 'parts',
    canCreate: true,
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'quantity', label: 'Qty' },
      { key: 'location', label: 'Location' },
    ],
    createBody: () => ({ sku: `SKU-${Date.now().toString().slice(-5)}`, name: 'Spare Part', category: 'general', quantity: 1, location: 'Warehouse', reorderLevel: 5 }),
  },
  {
    id: 'chemicals',
    label: 'Chemicals',
    singular: 'Chemical',
    endpoint: '/api/company/chemicals',
    listKey: 'chemicals',
    canCreate: true,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'sku', label: 'SKU' },
      { key: 'quantity', label: 'Qty' },
      { key: 'unit', label: 'Unit' },
      { key: 'location', label: 'Location' },
    ],
    createBody: () => ({ name: 'Chemical Product', sku: `CH-${Date.now().toString().slice(-4)}`, quantity: 0, unit: 'gal', location: 'Chem shed', reorderLevel: 10 }),
  },
  {
    id: 'jsa',
    label: 'JSA Forms',
    singular: 'JSA',
    endpoint: '/api/company/jsa-forms',
    listKey: 'forms',
    canCreate: false,
    columns: [
      { key: 'title', label: 'Title' },
      { key: 'status', label: 'Status' },
      { key: 'createdAt', label: 'Created' },
    ],
    createBody: () => ({}),
  },
]

const validTabIds = new Set(tabs.map((t) => t.id))

function tabFromQuery(): TabId {
  const q = String(route.query.tab || '')
  return validTabIds.has(q as TabId) ? (q as TabId) : 'work-orders'
}

const activeTab = ref<TabId>(tabFromQuery())
const rows = ref<Record<string, any>[]>([])
const counts = ref<Record<string, number>>({})
const loading = ref(false)
const deletingId = ref<number | null>(null)
const error = ref('')

const currentTab = computed(() => tabs.find((t) => t.id === activeTab.value)!)

function authHeaders() {
  const token = localStorage.getItem('auth_token') || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function loadTab(id: TabId = activeTab.value) {
  const tab = tabs.find((t) => t.id === id)!
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(tab.endpoint, { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const list = Array.isArray(data) ? data : data[tab.listKey] || []
    rows.value = list
    counts.value = { ...counts.value, [id]: list.length }
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function switchTab(id: TabId) {
  activeTab.value = id
  if (route.query.tab !== id) {
    router.replace({ path: route.path, query: { ...route.query, tab: id } })
  }
  await loadTab(id)
}

async function createItem() {
  const tab = currentTab.value
  if (!tab.canCreate) return
  if (tab.id === 'field-tickets') {
    router.push('/company/field-tickets')
    return
  }
  try {
    const res = await fetch(tab.endpoint, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(tab.createBody()),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await loadTab()
  } catch (e: any) {
    error.value = e.message || 'Create failed'
  }
}

function canDelete(row: Record<string, any>) {
  if (activeTab.value === 'work-orders') return row.status === 'draft'
  if (activeTab.value === 'field-tickets') return ['open', 'in_progress'].includes(row.status)
  return true
}

async function deleteItem(row: Record<string, any>) {
  const tab = currentTab.value
  const label = row.name || row.title || row.number || row.unitNumber || row.loadNumber || row.sku || `${tab.singular} #${row.id}`
  if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return
  deletingId.value = Number(row.id)
  error.value = ''
  try {
    const res = await fetch(`${tab.endpoint}/${row.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    rows.value = rows.value.filter((item) => item.id !== row.id)
    counts.value = { ...counts.value, [tab.id]: rows.value.length }
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    deletingId.value = null
  }
}

watch(
  () => route.query.tab,
  async () => {
    const next = tabFromQuery()
    if (next !== activeTab.value) {
      activeTab.value = next
      await loadTab(next)
    }
  }
)

onMounted(async () => {
  activeTab.value = tabFromQuery()
  await loadTab(activeTab.value)
  for (const tab of tabs) {
    if (tab.id === activeTab.value) continue
    try {
      const res = await fetch(tab.endpoint, { headers: authHeaders() })
      if (!res.ok) continue
      const data = await res.json()
      const list = Array.isArray(data) ? data : data[tab.listKey] || []
      counts.value = { ...counts.value, [tab.id]: list.length }
    } catch {
      /* ignore */
    }
  }
})
</script>
